import { useParams } from 'next/navigation';
import { FC } from 'react';
import { Accordion, AccordionItem } from '@/components/ui';
import { RenderableImage } from '@/components/ui/image/renderable-image';
import useCoreConfig from '@/hooks/use-core-config';
import useProductDetailPageContext from '@/hooks/use-product-detail-page-context';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';

const GeneralInfo: FC = () => {
  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak?.authenticated;
  const { features } = useCoreConfig();
  const { productDetailContent } = useProductDetailPageContext();
  const { previewLocale } = useAppContext<{ previewLocale?: string }>();
  const params = useParams<{ locale?: string }>();
  const locale = params.locale ?? previewLocale ?? 'ro';

  if (!productDetailContent) {
    return null;
  }

  const allKeys = new Set(productDetailContent.sections.map(section => section.title));
  const trustSignals = productDetailContent.trustSignals ?? [];

  return (
    <>
      <Accordion
        itemClasses={{
          title: 'text-default-800',
          content: 'pt-0 pb-6 text-base text-default-500',
        }}
        selectionMode="multiple"
        defaultExpandedKeys={
          (!isAuthenticated && features.cartForGuests) || isAuthenticated ? [] : allKeys
        }
      >
        {productDetailContent.sections.map(({ title, items }) => (
          <AccordionItem key={title} title={title}>
            <ul className="list-inside list-disc">
              {items.map(item => (
                <li key={item} className="text-gray-600">
                  {item}
                </li>
              ))}
            </ul>
          </AccordionItem>
        ))}
      </Accordion>
      {trustSignals.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {trustSignals.map(({ image, link, alt }, index) => {
            const img = (
              <RenderableImage
                key={index}
                src={image}
                alt={alt || `Trust signal ${index + 1}`}
                className="h-24 w-auto object-contain"
              />
            );
            if (link) {
              const isExternal = link.startsWith('http');
              const href = isExternal
                ? link
                : `/${locale}${link.startsWith('/') ? '' : '/'}${link}`;
              return (
                <a key={index} href={href} target="_blank" rel="noopener">
                  {img}
                </a>
              );
            }
            return img;
          })}
        </div>
      )}
    </>
  );
};

export default GeneralInfo;
